import React, { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog.tsx";
import { cn } from "@/lib/utils";
import { Formik, Form, Field } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import hotspringLogo from "@/assets/logo.svg";
import { getGraphQLURL } from "@/env.ts";
import { LOGIN_URL } from "@/helpers/url";
import { InputField } from "@/components/common/InputField/InputField";

const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email) {
      success
      message
    }
  }
`;

const forgotPasswordSchema = z.object({
  email: z.string().email("Enter a valid email").min(1, "Email is required"),
});

interface FormValues {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [forgotPassword] = useMutation(FORGOT_PASSWORD_MUTATION, {
    context: { uri: getGraphQLURL() },
  });
  const [showSuccessView, setShowSuccessView] = useState(false);

  return (
    <div className="dark w-full h-[100vh] py-[10vh] bg-background flex items-start justify-start">
      <div className="flex w-full justify-center">
        <img src={hotspringLogo} alt="Hotspring Logo" />
      </div>
      <Dialog open={true}>
        <DialogContent
          className={cn(
            "dark p-4 rounded-[4px] bg-primary focus:outline-none flex flex-col gap-6",
            "border border-[#2D2D2D] outline-none w-[422px]",
          )}
        >
          <Formik<FormValues>
            initialValues={{ email: "" }}
            validationSchema={toFormikValidationSchema(forgotPasswordSchema)}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={async (values, { setSubmitting, setFieldError }) => {
              try {
                const { data } = await forgotPassword({ variables: values });
                if (data?.forgotPassword.success) {
                  setShowSuccessView(true);
                } else {
                  setFieldError(
                    "email",
                    data?.forgotPassword.message || "Request failed",
                  );
                }
              } catch (err) {
                setFieldError("email", "An unexpected error occurred.");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, values }) => (
              <Form className="flex flex-col gap-6">
                {showSuccessView ? (
                  <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2 pr-4">
                      <h5 className="text-[20px] text-white">
                        Verification link sent
                      </h5>
                      <p className="text-sm text-[#c3c3c3]">
                        We will share a verification link to&nbsp;
                        <span className="text-white">{values.email}</span>
                        &nbsp;with instructions to reset your password.
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <div className="h-px w-full bg-[#2D2D2D]" />
                      <Link to={LOGIN_URL} className="text-white">
                        Back to login
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <h5 className="text-[20px] text-white">Reset password</h5>
                      <p className="text-sm text-[#c3c3c3]">
                        We will send a verification link to your email to reset
                        password and sign in again
                      </p>
                    </div>
                    <Field
                      name="email"
                      type="email"
                      label="Email"
                      placeholder="your registered email"
                      component={InputField}
                      inputClassName="!h-10 !bg-transparent text-white"
                      iconClassName="!top-[38px]"
                    />
                    <DialogFooter className="flex !flex-col items-center gap-[12px]">
                      <div className="h-px w-full bg-[#2D2D2D]" />
                      <div className="w-full flex justify-end items-center gap-6">
                        <Link to={LOGIN_URL} className="text-white">
                          Back
                        </Link>
                        <Button
                          type="submit"
                          className="bg-service-paint text-white p-3 rounded hover:bg-service-paint hover:opacity:80 disabled:opacity-50"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Send"}
                        </Button>
                      </div>
                    </DialogFooter>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForgotPassword;
